import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import ReactTable from 'react-table';

import NoDataImg from '../../assets/img/no-data.svg';
import Card from '../Card/Card';
import CardBody from '../Card/CardBody';
import { DateInput } from '../DateInput';
import Snackbar from '../Snackbar/Snackbar';
import { NoDataWrapper, useTableDateRangeStyles } from './styles';


let timeout;

const DATE_PICKER_TYPES = Object.freeze({
  from: 'from',
  to  : 'to',
});

const NoData = () => (
  <NoDataWrapper id="noData">
    <img src={NoDataImg} height="84px" alt="no data" />
    <h4>No data</h4>
    <p>Change the dates range or adjust the filters.</p>
  </NoDataWrapper>
);

const validDates  = (type, from, to) => current => (
  type === DATE_PICKER_TYPES.from
    ? current.isBefore(to)
    : current.isAfter(from));


const isValidPage = (page, pageSize, total) => {
  const pages = Math.ceil(total / pageSize);
  const isValid = !Number.isNaN(page) && page <= pages;
  return isValid;
};


const TableDateRange = ({
  data,
  filters : {
    from,
    to,
    filtered: filteredFields,
    pageSize : filterPageSize,
  },
  dataFetch,
  loading,
  columns,
  total,
  defaultPageSize,
  filterFetchTimeout,
}) => {
  const classes = useTableDateRangeStyles();

  const onFetchData = useCallback(({
    filtered, page, sorted, pageSize,
  }) => {
    const isValid = isValidPage(page, pageSize, total);
    if (isValid) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        dataFetch({
          filtered,
          from,
          page,
          pageSize,
          sorted,
          to,
        });
      }, filterFetchTimeout);
    }
  }, [dataFetch, filterFetchTimeout, from, to, total]);


  const onDateChange = useCallback(({ _d: selectedDate }, type) => {
    const timestamp = (type === DATE_PICKER_TYPES.from
      ? moment(selectedDate).startOf('day')
      : moment(selectedDate).endOf('day')
    ).valueOf();
    // CAREFUL !! IF you change the order of the from to here it will CRASH => BWTF
    dataFetch({
      from,
      to,
      filtered: filteredFields,
      [type]  : timestamp,
    });
  }, [dataFetch, from, to, filteredFields]);

  return (
    <>
      <div className={classes.dateInputContainer}>
        <DateInput
          type={DATE_PICKER_TYPES.from}
          labelText="From"
          defaultValue={from}
          validator={validDates(DATE_PICKER_TYPES.from, from, to)}
          onChange={props => onDateChange(props, DATE_PICKER_TYPES.from)}
        />
        <DateInput
          type={DATE_PICKER_TYPES.to}
          labelText="To"
          defaultValue={to}
          validator={validDates(DATE_PICKER_TYPES.to, from, to)}
          onChange={props => onDateChange(props, DATE_PICKER_TYPES.to)}
        />
      </div>
      <Card hideOverflow className={classes.card}>
        <CardBody className={classes.tableWrapper}>
          <ReactTable
            className={classes.table}
            data={data}
            columns={columns}
            defaultPageSize={filterPageSize || defaultPageSize}
            filterable
            loading={loading}
            manual
            onFetchData={onFetchData}
            pages={Math.ceil(total / filterPageSize)}
            minRows={0}
            showPaginationTop={false}
            showPaginationBottom={total !== 0}
            NoDataComponent={NoData}
            defaultFiltered={filteredFields}
          />
        </CardBody>
      </Card>
      <Snackbar
        color="danger"
        message={data.error || ''}
        open={Boolean(data.error)}
      />
    </>
  );
};

TableDateRange.propTypes = {
  dataFetch: PropTypes.func.isRequired,
  filters  : PropTypes.shape({
    from    : PropTypes.number,
    to      : PropTypes.number,
    pageSize: PropTypes.number,
    filtered: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  total             : PropTypes.number,
  defaultPageSize   : PropTypes.number,
  filterFetchTimeout: PropTypes.number,
  loading           : PropTypes.bool.isRequired,
  data              : PropTypes.arrayOf(PropTypes.object).isRequired,
  columns           : PropTypes.arrayOf(PropTypes.object).isRequired,
};

TableDateRange.defaultProps = {
  total             : 0,
  defaultPageSize   : 10,
  filterFetchTimeout: 350,
};


export default TableDateRange;
